// apps/admin/src/features/notifications/useNotificationFeed.ts
//
// vg-binding: CONTEXT:decisions           (P15.D-18 notification.new + version-floor, P15.D-21 signal-only WS + fail-closed + optimistic, P15.D-07 membership events; Task 25)
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// REST hydrate + WS refresh for the TopBar notification bell.
//
// Fixes issue #2555 (UAT-SUP-012 steps 2-3): the bell never showed anything.
//
// `NotificationBell` renders from `useNotificationStore.items`, and the store
// exposes `setItems` documented as "Replace full list (initial REST fetch)" —
// but nothing ever called it. The only writer was
// `ws-system-channel.ts:282 → pushNotification`, and that subscriber is gated
// superadmin-only (`isSA` check at ws-system-channel.ts:267) and carries only
// `system.*` announcements. So for a plain `admin` role the store stayed empty
// forever: badge hidden, dropdown "empty" — even though
// `GET /api/v1/notifications` was serving rows.
//
// (The sibling `NotificationCenter` component DOES fetch correctly, but it is
// not what TopBar mounts — App.tsx:951 / TopBar.tsx:201 render
// `NotificationBell`.)
//
// This hook closes that gap the same way merchant-v3 already does
// (`apps/merchant-v3/src/components/NotificationBell.tsx`): fetch on mount,
// then refetch when the server pushes `notification.*`.
//
// Endpoint choice — `/api/v1/notifications`, NOT `/api/v1/admin/notifications`:
//   The admin-scoped feed filters `recipient_type: 'ADMIN'`
//   (notifications-feed.ts:196), but the persist worker never sets that field
//   (notification.worker.ts:181) so the schema default `'MERCHANT'`
//   (models/Notification.ts:448) applies to EVERY row produced through
//   `notificationService.fanout()` — including ones addressed to admin users.
//   The admin feed therefore returns 0 rows regardless of data (verified live
//   2026-07-27). `/api/v1/notifications` pins `userId` only, so it returns the
//   caller's real notifications. Fixing `recipient_type` end-to-end is a
//   separate, wider change (it also affects that feed's mark-read/dismiss
//   routes and needs a backfill) — tracked as a follow-up finding.
//
// Because we bypass the admin feed, its server-side `mapCategory()` is not
// available; `toBucket()` below mirrors that mapping on the client so bell
// tabs still group correctly.

import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@printway/ui';

import * as wsClient from '../../lib/wsClient.js';
import { apiClient, ApiError } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { isNotificationBundleStale } from './wsVersionFloor.js';
import {
  useNotificationStore,
  type AdminNotification,
  type NotificationCategory,
} from '../../stores/useNotificationStore.js';

// ---------------------------------------------------------------------------
// Wire shape — `/api/v1/notifications` (notification.handler.ts)
// ---------------------------------------------------------------------------

interface WireNotification {
  id?: unknown;
  _id?: unknown;
  category?: unknown;
  title?: unknown;
  body?: unknown;
  isRead?: unknown;
  is_read?: unknown;
  createdAt?: unknown;
  created_at?: unknown;
  link_url?: unknown;
  actionUrl?: unknown;
  priority?: unknown;
  i18n_key?: unknown;
  i18nKey?: unknown;
  i18n_params?: unknown;
  i18nParams?: unknown;
}

/** Rows the bell keeps cached. Server caps well below the store's own cap. */
const FEED_LIMIT = 25;

/**
 * How long to wait after a `ticket.*` WS frame before re-reading the feed.
 * Covers the BullMQ `notification.persist` round-trip (enqueue → worker →
 * insert) that has to finish before the row is fetchable.
 */
const PERSIST_SETTLE_MS = 1_500;

/**
 * Map a stored free-form category (`support`, `order.waiting_sla_reminder`,
 * `WALLET_TOPUP_CONFIRMED`, …) to one of the bell's 6 buckets.
 *
 * Kept byte-for-byte in step with the server-side `mapCategory()` in
 * `apps/api/src/modules/admin/handlers/notifications-feed.ts:100` so the two
 * surfaces never disagree about which tab a row belongs to. Unknown → system,
 * which is also where `support` lands (the bell has no Support tab; the
 * sidebar Support badge is a separate, ticket-count-driven surface).
 */
export function toBucket(raw: string): NotificationCategory {
  const lower = raw.toLowerCase();
  if (lower.startsWith('order') || lower.includes('shipment')) return 'orders';
  if (lower.startsWith('product') || lower.includes('blueprint')) {
    return 'products';
  }
  if (lower.startsWith('store') || lower.includes('shopify')) return 'stores';
  if (lower.startsWith('vendor') || lower.includes('vendor_approval')) {
    return 'vendors';
  }
  if (
    lower.includes('billing') ||
    lower.includes('invoice') ||
    lower.includes('wallet') ||
    lower.includes('payment') ||
    lower.includes('topup') ||
    lower.includes('withdraw')
  ) {
    return 'billing';
  }
  return 'system';
}

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

/** Normalise one wire row into the store's shape. Returns null if unusable. */
export function toAdminNotification(
  row: WireNotification,
): AdminNotification | null {
  const id = asString(row.id) || asString(row._id);
  if (!id) return null;

  const createdAt =
    asString(row.createdAt) ||
    asString(row.created_at) ||
    new Date(0).toISOString();

  // `priority: 'critical'` (or legacy 'urgent'/'high') drives the red dot +
  // the desktop-notification path in NotificationBell.
  const priority = asString(row.priority).toLowerCase();
  const severity: AdminNotification['severity'] =
    priority === 'critical' || priority === 'urgent' ? 'critical' : 'normal';

  const href = asString(row.link_url) || asString(row.actionUrl);

  // Carried so the bell can re-render title/body at the ACTIVE locale instead
  // of showing the dispatch-time language forever (see lib/notificationsI18n).
  const i18nKey = asString(row.i18n_key) || asString(row.i18nKey);
  const rawParams = row.i18n_params ?? row.i18nParams;
  const i18nParams =
    rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams)
      ? (rawParams as Record<string, unknown>)
      : null;

  const base: AdminNotification = {
    id,
    category: toBucket(asString(row.category, 'system')),
    title: asString(row.title),
    body: asString(row.body),
    severity,
    isRead: row.isRead === true || row.is_read === true,
    createdAt,
    ...(i18nKey ? { i18nKey } : {}),
    ...(i18nParams ? { i18nParams } : {}),
  };
  return href ? { ...base, href } : base;
}

interface FeedResult {
  items: AdminNotification[];
}

/**
 * `apiClient.get` already unwraps the `{ success, data }` envelope and throws
 * `ApiError` on non-2xx, so the payload here is the inner object. Tolerate a
 * still-wrapped shape anyway — a future envelope change should degrade to an
 * error we can see, not a silently blank bell.
 */
async function fetchFeed(): Promise<FeedResult> {
  const payload = await apiClient.get<unknown>('/api/v1/notifications', {
    params: { limit: FEED_LIMIT },
  });
  const outer = payload as
    | { notifications?: unknown; data?: { notifications?: unknown } }
    | undefined;
  const list = outer?.notifications ?? outer?.data?.notifications;
  if (!Array.isArray(list)) {
    throw new Error('MALFORMED_RESPONSE');
  }
  const items = list
    .map((r) => toAdminNotification(r as WireNotification))
    .filter((n): n is AdminNotification => n !== null);
  return { items };
}

// ---------------------------------------------------------------------------
// Server-side read mutations
//
// The store's `markRead` / `markAllRead` only mutate local state. Without a
// server hop the 30s refetch below would resurrect every row as unread, so the
// bell's read actions have to persist. Exported for `NotificationBell` to call
// alongside the optimistic store update.
// ---------------------------------------------------------------------------

/** PATCH /api/v1/notifications/:id/read — persist a single read. */
export async function markNotificationReadOnServer(id: string): Promise<void> {
  await apiClient.patch(
    `/api/v1/notifications/${encodeURIComponent(id)}/read`,
  );
}

/** PATCH /api/v1/notifications/read-all — persist bulk read (rate-limited 10/5min BE-side). */
export async function markAllNotificationsReadOnServer(): Promise<void> {
  await apiClient.patch('/api/v1/notifications/read-all');
}

/**
 * Hydrates `useNotificationStore` from REST and keeps it fresh over WS.
 *
 * Mount ONCE near the shell root (alongside `SystemEventListener`) — the store
 * is a singleton, so a second mount would only duplicate fetches.
 *
 * `notification.new` / `.read` / `.read_all` ride the server-side
 * `ws:user:{userId}` channel (wsClient.ts:117-119 map them to `null`, i.e. no
 * explicit subscribe), so this works for every admin role — no superadmin gate,
 * unlike the `system.*` path.
 */
export function useNotificationFeed(): void {
  const qc = useQueryClient();
  const { t } = useTranslation('admin');
  const { toast } = useToast();
  const setItems = useNotificationStore((s) => s.setItems);
  const setLoading = useNotificationStore((s) => s.setLoading);
  const setError = useNotificationStore((s) => s.setError);
  const setForbidden = useNotificationStore((s) => s.setForbidden);

  const query = useQuery<FeedResult>({
    queryKey: ['admin', 'notifications', 'bell'],
    queryFn: fetchFeed,
    // Poll as a safety net for a dropped WS frame — same 30s cadence the
    // sidebar Support badge already uses (useSupportTicketRealtime.ts:66).
    refetchInterval: 30_000,
    staleTime: 15_000,
    // Fail-closed retry (P15.D-21): 401/403 are terminal (auth / audience) —
    // never re-hit; a 403 AUDIENCE_FORBIDDEN must surface the banner promptly.
    retry: (failureCount, error) => {
      if (
        error instanceof ApiError &&
        (error.status === 401 || error.status === 403)
      ) {
        return false;
      }
      return failureCount < 1;
    },
  });

  // Mirror query state into the store so the bell renders from one source.
  useEffect(() => {
    setLoading(query.isLoading);
  }, [query.isLoading, setLoading]);

  useEffect(() => {
    if (query.data) {
      // 200 (rows OR empty) → clear the fail-closed flag; the bell decides
      // between list + real empty state purely on `items.length` (P15.D-21).
      setItems(query.data.items);
      setError(null);
      setForbidden(false);
    }
  }, [query.data, setItems, setError, setForbidden]);

  useEffect(() => {
    if (!query.error) return;
    // 2-case revoke (P15.D-21 / P15.D-07): a 403 AUDIENCE_FORBIDDEN feed is NOT
    // a generic error and NOT an empty state — it drives a banner + retry.
    //
    // 403-semantic (post-review FA): the BE now returns 403 AUDIENCE_FORBIDDEN
    // ONLY for a genuine no-identity token; an org-revoked user gets 200 with a
    // PERSONAL feed instead. So this branch is a rare hard-error path — the
    // revoke UX flows through membership.revoked → refetch → 200 personal feed.
    if (
      query.error instanceof ApiError &&
      query.error.status === 403 &&
      query.error.code === 'AUDIENCE_FORBIDDEN'
    ) {
      // M6: fail-close the badge regardless of the detection path. A poll-driven
      // 403 (after a prior 200) leaves `query.data` holding the last rows, so the
      // success effect never re-fires to clear them — without this the bell badge
      // stays at the stale unread count UNDER the forbidden banner. Clearing
      // items here drives `unreadCount` (derived from items) to 0.
      setItems([]);
      setForbidden(true);
      setError(null);
      return;
    }
    setForbidden(false);
    setError(
      query.error instanceof Error ? query.error.message : 'FETCH_FAILED',
    );
  }, [query.error, setError, setForbidden, setItems]);

  // WS → refetch. Server is authoritative for read-state and ordering, so we
  // refetch rather than patching the store from the frame payload.
  const refetch = useCallback((): void => {
    void qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'bell'] });
  }, [qc]);

  // Signal-only notification handler (P15.D-21): refetch on the frame AND run
  // the version-floor check (P15.D-18) so an old bundle is prompted to reload.
  const onNotificationSignal = useCallback(
    (frame: wsClient.WsFrame): void => {
      if (isNotificationBundleStale(frame.payload)) {
        toast({
          variant: 'default',
          title: t('chrome.notificationBell.reloadPrompt'),
        });
      }
      refetch();
    },
    [refetch, toast, t],
  );

  // membership.revoked (P15.D-07): bust the badge immediately, then refetch —
  // the refetch's HTTP status decides the view (403 banner / 200 empty / rows).
  const onMembershipRevoked = useCallback((): void => {
    setItems([]);
    refetch();
  }, [setItems, refetch]);

  useEffect(() => {
    // Support ticket frames are a SECOND, earlier wake-up for the bell.
    //
    // `notification.new` only fires after the whole persist round-trip:
    // request → BullMQ `notification.persist` → worker persists the row →
    // worker publishes. `ticket.created` / `ticket.reply` are published
    // straight from the request path — the same frames the sidebar Support
    // badge listens to (features/support/useSupportTicketRealtime.ts), which
    // is why the badge and the tab-title count moved while the bell sat still
    // (dbg-20260729-065255-9904).
    //
    // The row does NOT exist yet when the ticket frame lands, so refetching on
    // it alone would return the same list. We therefore schedule ONE follow-up
    // refetch past the typical queue round-trip. If `notification.new`
    // arrives first it refetches anyway and this one is a cheap no-op; if that
    // frame is dropped, this closes the gap that would otherwise wait out the
    // 30s poll.
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const refetchAfterPersist = (): void => {
      const id = setTimeout(() => {
        timers.delete(id);
        refetch();
      }, PERSIST_SETTLE_MS);
      timers.add(id);
    };

    const offs = [
      // Canonical signal (P15.D-18) — signal-only refetch. The legacy
      // `notification.create` dual-listen was REMOVED in Task 29 alongside the
      // server-side emitter (G-35): 0 `.on('notification.create')` remains.
      wsClient.subscribe('notification.new', onNotificationSignal),
      wsClient.subscribe('notification.read', refetch),
      wsClient.subscribe('notification.read_all', refetch),
      // Membership lifecycle (P15.D-07): revoke busts the badge + refetches
      // (status decides view); grant just refetches so a newly-granted audience
      // populates without a manual reload.
      wsClient.subscribe('membership.revoked', onMembershipRevoked),
      wsClient.subscribe('membership.granted', refetch),
      wsClient.subscribe('ticket.created', refetchAfterPersist),
      wsClient.subscribe('ticket.reply', refetchAfterPersist),
    ];
    return () => {
      for (const t of timers) clearTimeout(t);
      timers.clear();
      for (const off of offs) {
        try {
          off();
        } catch {
          /* unsubscribe must never throw */
        }
      }
    };
  }, [refetch, onNotificationSignal, onMembershipRevoked]);
}

export default useNotificationFeed;
